import { Flow, SelectHelperMenuItem, Text, useLocale } from 'bold-ui'
import { CustomSelectFieldProps, SelectField } from 'components/form/core'
import { HLabel } from 'components/HLabel'
import { CiapSelectFieldDocument } from 'graphql/hooks.generated'
import { CiapQueryInput, CiapSelectFieldQuery, CiapSelectFieldQueryVariables } from 'graphql/types.generated'
import { CypressProps } from 'types/cypress'

import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'

export type CiapSelectFieldModel = NonNullable<CiapSelectFieldQuery['ciaps']['content'][0]>

export const ciapItemToString = (item: CiapSelectFieldModel) => item && `${item.descricao} - ${item.codigo}`

export const ciapRenderItem = (item: CiapSelectFieldModel) => (
  <Flow direction='vertical' gap={0} data-testid='CiapSelectField.item'>
    <Text fontWeight='bold'>{item.descricao}</Text>
    <Flow direction='horizontal' gap={0.5}>
      <Text fontWeight='bold'>Código</Text>
      {item.codigo}
    </Flow>
    {item.inclui && <HLabel title='Inclui:'>{item.inclui}</HLabel>}
    {item.exclui && <HLabel title='Exclui:'>{item.exclui}</HLabel>}
  </Flow>
)

export type CiapSelectFieldProps = CustomSelectFieldProps<CiapSelectFieldModel> &
  Pick<
    CiapQueryInput,
    | 'sexo'
    | 'capitulosIncluidos'
    | 'capitulosExcluidos'
    | 'excluirCIAPsDAB'
    | 'ciapComCid10Encaminhamento'
    | 'idadeCidadaoEmAnos'
  > &
  CypressProps & {
    fetchCID10Encaminhamento?: boolean
  }

export function CiapSelectField(props: Readonly<CiapSelectFieldProps>) {
  const {
    sexo,
    capitulosIncluidos,
    capitulosExcluidos,
    excluirCIAPsDAB,
    ciapComCid10Encaminhamento,
    idadeCidadaoEmAnos,
    fetchCID10Encaminhamento = false,
    dataTestId,
    ...rest
  } = props

  const locale = useLocale()

  const { skipping, selectProps } = useAsyncQuerySelect<
    CiapSelectFieldModel,
    CiapSelectFieldQuery,
    CiapSelectFieldQueryVariables
  >({
    query: CiapSelectFieldDocument,
    extractItems: (data) => data?.ciaps?.content,
    variables: (inputQuery: string): CiapSelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        pageParams: DEFAULT_SELECT_PAGE_PARAM,
        sexo,
        capitulosIncluidos,
        capitulosExcluidos,
        excluirCIAPsDAB,
        ciapComCid10Encaminhamento,
        idadeCidadaoEmAnos,
      },
      fetchCID10Encaminhamento,
    }),
    skip: (inputString) => inputString?.trim()?.length < 2,
    debounceTime: 500,
  })

  return (
    <SelectField<CiapSelectFieldModel>
      itemToString={ciapItemToString}
      renderItem={ciapRenderItem}
      components={{
        EmptyItem: () => (
          <SelectHelperMenuItem>
            {skipping ? 'Digite para buscar um CIAP 2.' : locale.select.emptyItem}
          </SelectHelperMenuItem>
        ),
      }}
      dataTestId={dataTestId}
      {...selectProps}
      {...rest}
    />
  )
}
